"use client";

import type React from "react";
import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, MapPin, ChevronDown, ChevronRight } from "lucide-react";
import { PRODUCT_CATEGORY_STRUCTURE } from "../data/ProductCategories";

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeSubcategory, setActiveSubcategory] = useState<string | null>(null);
  const productsRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking elsewhere on the page
  const closeMenus = () => {
    setIsProductsOpen(false);
    setActiveCategory(null);
    setActiveSubcategory(null);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (productsRef.current && !productsRef.current.contains(event.target as Node)) {
        setIsProductsOpen(false);
        setActiveCategory(null);
        setActiveSubcategory(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCategoryMouseEnter = (category: string) => {
    setActiveCategory(category);
    setActiveSubcategory(null);
  };

  const handleSubcategoryMouseEnter = (subcategory: string) => {
    setActiveSubcategory(subcategory);
  };

  const getSubcategories = (category: string) =>
    PRODUCT_CATEGORY_STRUCTURE[category]?.subcategories || {};

  const getSubcategoryTypes = (category: string, subcategory: string): string[] => {
    const subcategories = getSubcategories(category);
    return subcategories[subcategory] || [];
  };

  const getProductsPath = ({
    category,
    subcategory,
    type,
  }: {
    category?: string;
    subcategory?: string;
    type?: string;
  }) => {
    const params = new URLSearchParams();

    if (category) {
      params.set("category", category);
    }

    if (subcategory) {
      params.set("subcategory", subcategory);
    }

    if (type) {
      params.set("type", type);
    }

    const query = params.toString();
    return query ? `/products?${query}` : "/products";
  };

  // Check if a subcategory has types
  const hasTypes = (category: string, subcategory: string) => {
    return getSubcategoryTypes(category, subcategory).length > 0;
  };

  return (
    <header className="w-full">
      {/* Top Bar */}
      <div className="bg-gray-900 text-white py-2">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center space-x-6">
            <div className="flex items-center text-sm">
              <MapPin size={16} className="mr-1" />
              <span>Canadian Warehousing & Delivery</span>
            </div>
          </div>
          <div className="text-sm">
            <Link to="/contact" className="hover:underline">
              Request a Quote
            </Link>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <div className="flex items-center">
                <img
                  src="/images/logo/logo.png"
                  alt="PES Lube Canada"
                  className="h-12"
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              <Link
                to="/"
                className="font-medium text-gray-800 hover:text-red-600 transition-colors"
                onClick={closeMenus}
              >
                Home
              </Link>
              {/* Products dropdown with nested subcategories */}
              <div className="relative" ref={productsRef}>
                <button 
                  className="font-medium text-gray-800 hover:text-red-600 transition-colors flex items-center"
                  onClick={() => setIsProductsOpen(!isProductsOpen)}
                >
                  Products <ChevronDown size={16} className="ml-1" />
                </button>
                
                {isProductsOpen && (
                  <div className="absolute top-full left-0 bg-white shadow-lg rounded-md mt-1 py-2 w-48 z-50 border border-gray-200">
                    <Link
                      to="/products"
                      className="block px-4 py-2 hover:bg-gray-100 text-gray-800 hover:text-red-600 transition-colors"
                      onClick={() => {
                        setIsProductsOpen(false);
                        setActiveCategory(null);
                        setActiveSubcategory(null);
                      }}
                    >
                      All Products
                    </Link>
                    
                    <div className="border-t my-1"></div>
                    
                    {/* Main categories with subcategories */}
                    {Object.keys(PRODUCT_CATEGORY_STRUCTURE).map((category) => (
                      <div 
                        key={category} 
                        className="relative"
                        onMouseEnter={() => handleCategoryMouseEnter(category)}
                      >
                        <Link
                          to={getProductsPath({ category })}
                          className="block px-4 py-2 hover:bg-gray-100 text-gray-800 hover:text-red-600 transition-colors flex justify-between items-center"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsProductsOpen(false);
                            setActiveCategory(null);
                            setActiveSubcategory(null);
                          }}
                        >
                          {category}
                          <ChevronRight size={14} />
                        </Link>
                        
                        {/* Subcategories panel */}
                        {activeCategory === category && (
                          <div className="absolute left-full top-0 bg-white shadow-lg rounded-md py-2 w-56 z-50 border border-gray-200">
                            {Object.keys(getSubcategories(category)).map(subcategory => (
                              <div
                                key={subcategory}
                                className="relative"
                                onMouseEnter={() => handleSubcategoryMouseEnter(subcategory)}
                              >
                                <Link
                                  to={getProductsPath({ category, subcategory })}
                                  className="block px-4 py-2 hover:bg-gray-100 text-gray-800 hover:text-red-600 transition-colors flex justify-between items-center"
                                  onClick={() => {
                                    setIsProductsOpen(false);
                                    setActiveCategory(null);
                                    setActiveSubcategory(null);
                                  }}
                                >
                                  {subcategory}
                                  {hasTypes(category, subcategory) && <ChevronRight size={14} />}
                                </Link>
                                
                                {activeSubcategory === subcategory && hasTypes(category, subcategory) && (
                                  <div className="absolute left-full top-0 bg-white shadow-lg rounded-md py-2 w-64 z-50 border border-gray-200">
                                    {getSubcategoryTypes(category, subcategory).map((type: string) => (
                                      <Link
                                        key={type}
                                        to={getProductsPath({ category, subcategory, type })}
                                        className="block px-4 py-2 hover:bg-gray-100 text-gray-800 hover:text-red-600 transition-colors"
                                        onClick={() => {
                                          setIsProductsOpen(false);
                                          setActiveCategory(null);
                                          setActiveSubcategory(null);
                                        }}
                                      >
                                        {type}
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Link
                to="/equivalent-guide"
                className="font-medium text-gray-800 hover:text-red-600 transition-colors"
                onClick={closeMenus}
              >
                Equivalent Guide
              </Link>
              <Link
                to="/about"
                className="font-medium text-gray-800 hover:text-red-600 transition-colors"
                onClick={closeMenus}
              >
                About Sinopec
              </Link>
              <Link
                to="/contact"
                className="font-medium text-gray-800 hover:text-red-600 transition-colors"
                onClick={closeMenus}
              >
                Contact
              </Link>
            </nav>

            {/* Right Icons */}
            <div className="flex items-center space-x-4">
              {/* 
              <button 
                className="text-gray-800 hover:text-red-600 transition-colors"
                onClick={closeMenus}
              >
                <Search size={20} />
              </button>
              */}
              <button
                className="lg:hidden text-gray-800 hover:text-red-600 transition-colors"
                onClick={() => {
                  setIsMenuOpen(true);
                  closeMenus();
                }}
              >
                <Menu size={24} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden">
          <div className="fixed right-0 top-0 h-full w-80 bg-white shadow-lg z-50 overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="font-bold text-lg">Menu</h2>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="text-gray-800 hover:text-red-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <nav className="p-4">
              <ul className="space-y-4">
                <li>
                  <Link
                    to="/"
                    className="block py-2 text-gray-800 hover:text-red-600 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Home
                  </Link>
                </li>
                {/* Mobile Products Menu */}
                <li className="border-b pb-2">
                  <div className="block py-2 text-gray-800 font-medium">Products</div>
                  <ul className="pl-4 pt-1 space-y-2">
                    <li>
                      <Link
                        to="/products"
                        className="block py-1 text-gray-700 hover:text-red-600 transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        All Products
                      </Link>
                    </li>
                    {Object.keys(PRODUCT_CATEGORY_STRUCTURE).map((category) => (
                      <li key={category}>
                        <Link
                          to={getProductsPath({ category })}
                          className="block py-1 text-gray-700 hover:text-red-600 transition-colors"
                          onClick={() => {
                            setIsMenuOpen(false);
                          }}
                        >
                          {category}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
                <li>
                  <Link
                    to="/equivalent-guide"
                    className="block py-2 text-gray-800 hover:text-red-600 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Equivalent Guide
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    className="block py-2 text-gray-800 hover:text-red-600 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    About Sinopec
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="block py-2 text-gray-800 hover:text-red-600 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
